import { getUserSession } from '@/service/UserService.js'
import { useKinectinStore } from '@/store/kinectin.js'
import { storeToRefs } from 'pinia'
import { linkIdToId } from '@/service/uiUtils.js'
import { DateTime } from 'luxon'
import { getPendingRequiredScreeners, requiredScreenerIsActiveForUser } from '@/service/requiredScreeners.js'

const unauthenticatedRoutes = [
    'signin',
    'signup',
    'signupwith-orgUrlSegment',
    'signinwith-orgUrlSegment',
    'privacy',
    'terms',
    'about',
    'auth_callback',
    'berightback',
]
const webAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]
const nativeAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]

function userCanAccessLeadershipDashboard({ orgId, store, userProfile }) {
    const tags = Array.isArray(userProfile.value?.tags) ? userProfile.value.tags : []
    return tags.includes(`${orgId}:role:Manager`) ||
        store.userCanManageOrgs() ||
        store.userHasPrivilege(`manageOrgId:${orgId}`)
}

function isRealRouteChange({ to, from }) {
    return to.name !== from.name || to.path !== from.path
}

function setupRouterMiddleware({ router }) {
    router.beforeEach(async (to, from, next) => {
        if (['berightback', 'privacy', 'terms', 'about', 'auth_callback'].includes(to.name)) return next()

        const store = useKinectinStore()
        const { maintenanceMode, userIsAuthenticated, userProfile } = storeToRefs(store)

        if (maintenanceMode.value) return next({ path: '/berightback', query: { redirect: to.fullPath } })

        if (to.name === 'signinwith-orgUrlSegment') { // special handling here
            const { token } = await getUserSession()
            if (token) return next(to.query.redirect)
        }

        const { token } = await getUserSession()
        if (token) userIsAuthenticated.value = true

        let title = to.path === '/' ? 'Kinectin' : `Kinectin - ${to.path}`
        const baselineOnboarding = to.name === 'assessments-baseline' && to.query.onboarding
        if (unauthenticatedRoutes.includes(to.name) || baselineOnboarding) {
            document.title = title
            return next()
        }
        if (!token) {
            await store.clearCoachGreet() // in case a different user logs in
            if (['signupwith-orgUrlSegment', 'signinwith-orgUrlSegment'].includes(to.name)) {
                return next()
            }
            return next({ path: '/signup', query: { redirect: to.fullPath } })
        }
        const orgCacheExpired = isRealRouteChange({ to, from }) && store.expireOrgCacheIfNeeded()
        await store.loadUserProfile({ noCache: orgCacheExpired })

        if (
            !userProfile.value.onboarded &&
            !['assessments-baseline', 'auth_callback', 'billing', 'onboard'].includes(to.name)
        ) {
            return next({ path: '/auth_callback' })
        }

        const fnsByRouteName = {
            'orgs': store.userCanManageOrgIds,
            'org-orgId': store.userCanManageOrgIds,
        }
        if (to.name === 'org-orgId-dashboard') {
            const orgId = linkIdToId({ linkId: to.params.orgId })
            if (!userCanAccessLeadershipDashboard({ orgId, store, userProfile })) {
                return next({ path: '/chat/new' })
            }
        }
        const fn = fnsByRouteName[to.name]
        if (fn && !fn()) return next({ path: '/chat/new' })

        // if the user is in range of a required screener, redirect to the screener if they haven't taken it yet
        const orgId = userProfile.value.orgId
        if (orgId && userProfile.value.onboarded) {
            const org = await store.getOrgPublicProfile({ orgId })
            const now = DateTime.now()
            const requiredScreeners = org.requiredScreenersConfig?.requiredScreeners || []
            if (
                to.name === 'screeners-screener' &&
                requiredScreeners.some((requiredScreener) =>
                    to.query.screenerId === requiredScreener.screenerId &&
                    requiredScreenerIsActiveForUser({ orgId, userProfile: userProfile.value, requiredScreener, now })
                )
            ) {
                return next()
            }
            const pending = await getPendingRequiredScreeners({
                store, orgId, userProfile: userProfile.value, requiredScreeners, now,
            })
            if (pending.length) return next({ path: '/screeners/screener', query: { orgId, screenerId: pending[0].screenerId, required: 1 } })
        }

        if (to.name === 'chat-threadId' && to.path !== '/chat/new') {
            const linkId = to.params.threadId
            const threadId = linkIdToId({ linkId })
            await store.selectThread({ threadId })
            const { selectedThread } = storeToRefs(store)
            title = `Kinectin - ${selectedThread.value.name}`
        }
        document.title = title
        return next()
    })
}

export {
    setupRouterMiddleware,
    unauthenticatedRoutes,
    webAppHideDrawerRoutes,
    nativeAppHideDrawerRoutes,
}
